import { nanoid } from 'nanoid';

import type { localStorage } from '@change-corgi/core/storage';

import type { DraftAdapter, DraftData, PetitionDraft } from '../types';

type Storage = typeof localStorage;

export class LocalStorageAdapter implements DraftAdapter {
	private readonly storage: Storage;
	private readonly storageKeyPrefix = 'draft';

	constructor(storage: Storage) {
		this.storage = storage;
	}

	async createDraft(authorId: string, data?: Partial<DraftData>): Promise<PetitionDraft> {
		this.assertStorageAvailable();

		try {
			const newDraft: PetitionDraft = { ...data, id: nanoid(), authorId, status: 'active', updatedAt: Date.now() };
			this.storage.setItem(this.getStorageKey(authorId), JSON.stringify(newDraft));
			return newDraft;
		} catch (error: unknown) {
			throw error instanceof Error ? error : new Error(String(error));
		}
	}

	async getDraft(authorId: string): Promise<PetitionDraft | null> {
		this.assertStorageAvailable();

		try {
			const draft = this.storage.getItem(this.getStorageKey(authorId));
			const parsedDraft = draft ? (JSON.parse(draft) as PetitionDraft) : null;
			return parsedDraft;
		} catch (error: unknown) {
			throw error instanceof Error ? error : new Error(String(error));
		}
	}

	async updateDraft(authorId: string, updates: Partial<PetitionDraft>): Promise<PetitionDraft> {
		this.assertStorageAvailable();

		const existingDraft = await this.getDraft(authorId);
		if (!existingDraft) throw new Error('Draft not found');

		const updatedDraft: PetitionDraft = { ...existingDraft, ...updates, updatedAt: Date.now() };
		this.storage.setItem(this.getStorageKey(authorId), JSON.stringify(updatedDraft));
		return updatedDraft;
	}

	async findOrCreateDraft(authorId: string, data?: Partial<DraftData>): Promise<PetitionDraft> {
		this.assertStorageAvailable();

		try {
			const existingDraft = await this.getDraft(authorId);
			if (existingDraft) return existingDraft;

			return this.createDraft(authorId, data);
		} catch (error: unknown) {
			throw error instanceof Error ? error : new Error(String(error));
		}
	}

	private getStorageKey(authorId: string): string {
		return `${this.storageKeyPrefix}-${authorId}`;
	}

	// core package silences storage access errors, but we need them to throw here as the draft client
	// relies on catching these errors to determine adapter availability/fallback behavior
	private assertStorageAvailable(): void {
		if (!this.storage.hasLocalStorage()) {
			throw new Error('Local storage is not available');
		}
	}
}
